import React, { useState, useEffect, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import "./App.css";

// Import components
import Navbar from "./components/Navbar";
import LandingPage from "./components/LandingPage";
import AuthPage from "./components/AuthPage";
import Dashboard from "./components/Dashboard";
import PropertyListing from "./components/PropertyListing";
import PropertyDetails from "./components/PropertyDetails";
import BookingPage from "./components/BookingPage";
import BookingManagement from "./components/BookingManagement";
import LoyaltyProgram from "./components/LoyaltyProgram";
import SubscriptionManager from "./components/SubscriptionManager";
import CustomerSupportChat from "./components/CustomerSupportChat";
import ReviewsPage from "./components/ReviewsPage";
import PaymentSuccess from "./components/PaymentSuccess";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth context
const AuthContext = createContext();

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(userData);
      
      toast.success('Login successful!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${API}/auth/register`, userData);
      const { access_token, user: newUser } = response.data;
      
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(newUser);
      
      toast.success('Registration successful!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    toast.success('Logged out successfully');
  };

  if (loading) {
    return (
      <div className="App">
        <div className="loading-screen">
          <div className="loading-spinner"></div>
          <p>Loading ZypeeTravel...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, register, logout }}>
      <div className="App">
        <BrowserRouter>
          <Navbar />
          <main className="main-content">
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/auth" element={!user ? <AuthPage /> : <Navigate to="/dashboard" />} />
              <Route 
                path="/dashboard" 
                element={user ? <Dashboard /> : <Navigate to="/auth" />} 
              />
              <Route path="/properties" element={<PropertyListing />} />
              <Route path="/properties/:id" element={<PropertyDetails />} />
              <Route 
                path="/book/:propertyId" 
                element={user ? <BookingPage /> : <Navigate to="/auth" />} 
              />
              <Route 
                path="/bookings" 
                element={user ? <BookingManagement /> : <Navigate to="/auth" />} 
              />
              <Route 
                path="/reviews" 
                element={user ? <ReviewsPage /> : <Navigate to="/auth" />} 
              />
              <Route 
                path="/payment-success" 
                element={user ? <PaymentSuccess /> : <Navigate to="/auth" />} 
              />
              <Route 
                path="/loyalty" 
                element={user ? <LoyaltyProgram /> : <Navigate to="/auth" />} 
              />
              <Route 
                path="/subscription" 
                element={user ? <SubscriptionManager /> : <Navigate to="/auth" />} 
              />
            </Routes>
          </main>

          {/* AI Customer Support Chatbot - Available on all pages */}
          <CustomerSupportChat />

          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </div>
    </AuthContext.Provider>
  );
}

export { AuthContext };
export default App;